import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import casaConstruida1 from "@/assets/casa-construida-1.jpg";
import casaConstruida1_2 from "@/assets/casa-construida-1-2.jpg";
import casaConstruida1_3 from "@/assets/casa-construida-1-3.jpg";
import casaConstruida1_4 from "@/assets/casa-construida-1-4.jpg";
import casaConstruida1_5 from "@/assets/casa-construida-1-5.jpg";
import casaConstruida1_6 from "@/assets/casa-construida-1-6.jpg";
import casaConstruida1_7 from "@/assets/casa-construida-1-7.jpg";
import casaConstruida1_8 from "@/assets/casa-construida-1-8.jpg";

interface PastProject {
  cover: string;
  title: string;
  images: string[];
}

// Plantillas de imágenes (sustituir por fotos reales más adelante)
const placeholderGallery = (seed: number) =>
  Array.from({ length: 10 }, (_, i) =>
    `https://images.unsplash.com/photo-${
      [
        "1600585154340-be6161a56a0c",
        "1600566753190-17f0baa2a6c3",
        "1600607687939-ce8a6c25118c",
        "1613490493576-7fde63acd811",
        "1600596542815-ffad4c1539a9",
        "1512917774080-9991f1c4c750",
        "1560448204-e02f11c3d0e2",
        "1571055107559-3e67626fa8be",
        "1600210492486-724fe5c67fb0",
        "1600573472550-8090b5e0745e",
      ][(seed + i) % 10]
    }?w=1400&q=80`
  );

const projects: PastProject[] = [
  {
    cover: casaConstruida1,
    title: "Casa Sercom — Proyecto 1",
    images: [
      casaConstruida1,
      casaConstruida1_2,
      casaConstruida1_3,
      casaConstruida1_4,
      casaConstruida1_5,
      casaConstruida1_6,
      casaConstruida1_7,
      casaConstruida1_8,
    ],
  },
];

export default function PastProjectsGallery() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [imgIdx, setImgIdx] = useState(0);

  const openProject = (i: number) => {
    setOpenIdx(i);
    setImgIdx(0);
  };
  const close = () => setOpenIdx(null);
  const prev = () =>
    openIdx !== null &&
    setImgIdx((i) => (i - 1 + projects[openIdx].images.length) % projects[openIdx].images.length);
  const next = () =>
    openIdx !== null && setImgIdx((i) => (i + 1) % projects[openIdx].images.length);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {projects.map((p, i) => (
          <motion.button
            key={i}
            onClick={() => openProject(i)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10 group relative cursor-pointer md:col-start-2"
          >
            <img
              src={p.cover}
              alt={p.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-4">
              <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Ver galería ({p.images.length})
              </span>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
              aria-label="Cerrar"
            >
              <X className="w-7 h-7" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 md:left-8 text-white/80 hover:text-white p-2 z-10"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 md:right-8 text-white/80 hover:text-white p-2 z-10"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <motion.div
              key={imgIdx}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
            >
              <img
                src={projects[openIdx].images[imgIdx]}
                alt={`${projects[openIdx].title} - imagen ${imgIdx + 1}`}
                className="max-h-[80vh] w-auto object-contain rounded-lg"
              />
              <p className="text-white/85 text-sm mt-4">
                {projects[openIdx].title} — {imgIdx + 1} / {projects[openIdx].images.length}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
